import React, { useMemo, useState } from "react"

import CatchModal from "~/library/CatchModal"

import { apiPayments } from "~/routes"
import { useToast } from "~/utils/Toast"
import getAppConfig from "~/utils/getAppConfig"
import { payableLabels } from "~/utils/paymentModalHelpers"
import usePaymentItemsState from "~/hooks/usePaymentItemsState"
import { calculateProcessingFeeCents } from "~/utils/processingFee"

import type PaymentItem from "~/types/PaymentItem"
import type { PayablePaymentResource } from "~/types/PayablePaymentResource"
import type { PaymentKind } from "~/components/payments/PayablePaymentModal"
import PaymentItemsTable from "~/components/payments/PaymentItemsTable"

interface Props {
  onClose: () => void
  onSuccess: () => void | Promise<void>
  resource: PayablePaymentResource
  initialItems?: PaymentItem[]
  showTypeSelector: boolean
  paymentType: PaymentKind
  onPaymentTypeChange: (value: PaymentKind) => void
}

const InvoicePaymentModal: React.FC<Props> = ({
  onClose,
  onSuccess,
  resource,
  initialItems = [],
  showTypeSelector,
  paymentType,
  onPaymentTypeChange,
}) => {
  const toast = useToast()
  const { venue, venueSlug, processingFeeRates } = getAppConfig()

  const currencyIso = resource.currency || "USD"
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { paymentItems, setPaymentItems, visibleItems, hasValidItems } = usePaymentItemsState({
    initialItems,
    currencyIso,
  })
  const processingFeeCents = useMemo(() => {
    const subtotalAmountCents = visibleItems.reduce(
      (total, item) => total + item.quantity * item.amountCents,
      0
    )

    return calculateProcessingFeeCents({
      catchMarkupFixedCents: venue.catchMarkupFixedCents,
      catchMarkupPercentage: venue.catchMarkupPercentage,
      currency: currencyIso,
      processingFeeRates,
      subtotalAmountCents,
    })
  }, [currencyIso, processingFeeRates, venue, visibleItems])
  const handleSubmit = () => {
    if (visibleItems.length === 0) {
      toast("At least one payment item is required", "danger")
      return
    }

    if (!hasValidItems) {
      toast("Please add at least one valid item with description and amount", "danger")
      return
    }

    setIsSubmitting(true)

    apiPayments
      .create({
        data: {
          venueSlug,
          payableId: resource.payableId,
          payableType: resource.payableType,
          payment: {
            type: "Invoice",
            paymentItemsAttributes: visibleItems,
          },
        },
      })
      .then(async () => {
        toast("Invoice created", "success")
        await onSuccess()
        onClose()
      })
      .catch((error) => {
        toast(error.body?.errors?.join(", ") || "Failed to create payment", "danger")
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <CatchModal
      isOpen={true}
      onClose={onClose}
      title={`Create Invoice — ${payableLabels[resource.payableType]}`}
      size="lg"
    >
      <div className="space-y-4">
        {showTypeSelector && (
          <div className="row mb-3">
            <div className="col-12 d-flex gap-2 align-items-center flex-wrap">
              <label className="form-label mb-0 text-muted fw-bold">Type</label>

              <select
                className="form-select w-auto"
                value={paymentType}
                onChange={(e) => onPaymentTypeChange(e.target.value as PaymentKind)}
                disabled={isSubmitting}
              >
                <option value="Invoice">Invoice (Stripe)</option>
                <option value="Manual">Manual (record paid)</option>
                <option value="SavedCard">Saved Card Charge</option>
              </select>
            </div>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-6">
            <h6 className="text-muted mb-2">INVOICE FOR</h6>

            <div>
              <strong>{resource.summary.title}</strong>
              <br />

              {resource.summary.subtitle && (
                <>
                  <span className="text-muted">{resource.summary.subtitle}</span>
                  <br />
                </>
              )}

              {resource.summary.meta && (
                <>
                  <span className="text-muted">{resource.summary.meta}</span>
                  <br />
                </>
              )}

              <span className="badge bg-secondary text-uppercase">
                {payableLabels[resource.payableType]}
              </span>
            </div>
          </div>

          <div className="col-6">
            {!resource.customer ? (
              <div className="alert alert-warning mb-0">No client information available.</div>
            ) : (
              <div className="row">
                <div className="col-12 mb-3">
                  <h6 className="text-muted mb-2">BILL TO</h6>
                  <div>
                    <strong>{resource.customer.name}</strong>
                    <br />

                    {resource.customer.email && (
                      <>
                        <span className="text-muted">{resource.customer.email}</span>
                        <br />
                      </>
                    )}

                    {resource.customer.phone && (
                      <span className="text-muted">{resource.customer.phone}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {resource.summary.details && (
            <div className="col-12 mt-2">
              <h6 className="text-muted mb-2">ADDITIONAL DETAILS</h6>
              <p className="text-muted mb-0">{resource.summary.details}</p>
            </div>
          )}
        </div>

        <PaymentItemsTable
          title="ITEMS TO INVOICE"
          items={paymentItems}
          setItems={setPaymentItems}
          currency={resource.currency || "USD"}
          isSubmitting={isSubmitting}
          processingFeeCents={processingFeeCents}
          processingFeePayer={venue.processingFeePayer}
          placeholderTitle={`Payment for ${resource.summary.title}`}
        />

        <div className="d-flex gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary ms-auto"
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={isSubmitting || !resource.customer || !hasValidItems}
          >
            {isSubmitting ? "Saving..." : "Create Invoice"}
          </button>
        </div>
      </div>
    </CatchModal>
  )
}

export default InvoicePaymentModal
